import {
    importProvidersFrom,
    inject,
    Injector,
    provideAppInitializer,
    provideZoneChangeDetection,
} from '@angular/core';
import { InMemoryScrollingOptions, provideRouter, withInMemoryScrolling, withPreloading } from '@angular/router';

import { LOCATION_INITIALIZED } from '@angular/common';
import {
    HTTP_INTERCEPTORS,
    HttpClient,
    HttpHandlerFn,
    HttpRequest,
    provideHttpClient,
    withInterceptors,
} from '@angular/common/http';
import { AuthHttpInterceptor } from '@auth0/auth0-angular';
import { CustomAura, provideAuth0Module } from '@core/config';
import { FlagBasedPreloadingStrategy } from '@core/helpers/preload-routes.helper';
import { authenticationInterceptor } from '@core/interceptors/authentication.interceptor';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { GeneralHelper } from '@shared/helpers';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { routes } from './app.routes';

export function HttpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http);
}

export const provideTranslation = () => ({
    defaultLanguage: 'en',
    loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
    },
});

let previousRoute = '';
const scrollConfig: InMemoryScrollingOptions = {
    get scrollPositionRestoration() {
        if (!previousRoute || previousRoute === window.location.pathname) {
            previousRoute = window.location.pathname;
            return 'disabled' as const;
        }
        previousRoute = window.location.pathname;
        return 'top' as const;
    },
    anchorScrolling: 'enabled',
};

function loadWithRetry(loadFn: () => Promise<any>, maxRetries = 3): Promise<any> {
    return loadFn().catch(error => {
        if (maxRetries > 0) {
            console.warn(`Translation loading failed, retrying... (${maxRetries} attempts left)`, error);
            return new Promise(resolve => setTimeout(() => resolve(loadWithRetry(loadFn, maxRetries - 1)), 1000));
        }
        throw error;
    });
}

function appInitializerFactory(translate: TranslateService, injector: Injector) {
    return () =>
        new Promise<any>(async (resolve: any) => {
            try {
                const locationInitialized = injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
                await locationInitialized;

                const langToSet = 'en';
                const translationParts = GeneralHelper.getTranslationFiles();
                translate.setDefaultLang('en');

                await loadWithRetry(
                    () =>
                        new Promise<void>((resolve, reject) => {
                            translate.use(langToSet).subscribe({
                                next: () => resolve(),
                                error: reject,
                                complete: () => resolve(),
                            });
                        })
                );

                for (const part of translationParts) {
                    try {
                        await loadWithRetry(
                            () =>
                                new Promise<void>((resolve, reject) => {
                                    translate.getTranslation('en-' + part).subscribe({
                                        next: translations => {
                                            translate.setTranslation('en', translations, true);
                                            resolve();
                                        },
                                        error: reject,
                                    });
                                })
                        );
                    } catch (error) {
                        console.error(`Failed to load translation file 'en-${part}':`, error);
                    }
                }

                resolve(null);
            } catch (error) {
                console.error('Translation initialization failed:', error);
                resolve(null);
            }
        });
}

const authInterceptor = (req: HttpRequest<any>, handle: HttpHandlerFn) =>
    inject(AuthHttpInterceptor).intercept(req, { handle });

export const appConfig = {
    providers: [
        MessageService,

        provideZoneChangeDetection(),
        importProvidersFrom([TranslateModule.forRoot(provideTranslation())]),
        provideAuth0Module(),

        provideAppInitializer(() => {
            const initializerFn = appInitializerFactory(inject(TranslateService), inject(Injector));
            return initializerFn();
        }),

        providePrimeNG({
            theme: {
                preset: CustomAura,
                options: {
                    darkModeSelector: '.dark-mode',
                },
            },
        }),

        AuthHttpInterceptor,
        { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
        provideHttpClient(withInterceptors([authInterceptor, authenticationInterceptor])),
        provideRouter(routes, withInMemoryScrolling(scrollConfig), withPreloading(FlagBasedPreloadingStrategy)),
    ],
};
